import type { LiqCluster, LiqMapSnapshot } from '@/lib/api/speed';

import type { PositionType } from '../trading/types';

/**
 * What the charts draw and what the legend says, derived from one snapshot.
 *
 * Everything here is arithmetic on a snapshot the server already computed;
 * the two charts (D3 live line, TradingView candles) render the same `Band`s so
 * they cannot disagree about where a zone is.
 */

export type Side = 'long' | 'short';

export interface Band {
    side: Side;
    price: number;
    low: number;
    high: number;
    notionalUsd: number;
    wallets: number;
    distancePct: number;
    /** 0..1, log-scaled against the biggest cluster on the map — drives opacity */
    intensity: number;
}

/** How many bands each chart draws per side. Beyond this a map is noise. */
export const BANDS_PER_SIDE = 6;

/**
 * Bands for the chart. Log-scaled intensity: the biggest cluster is usually
 * several times the next one, so a linear scale renders everything but the
 * top cluster as invisible.
 */
export function bandsFor(
    snapshot: LiqMapSnapshot | null | undefined,
    perSide = BANDS_PER_SIDE,
): Band[] {
    if (!snapshot) return [];
    const all = [
        ...snapshot.longs.slice(0, perSide).map((c) => toBand(c, 'long')),
        ...snapshot.shorts.slice(0, perSide).map((c) => toBand(c, 'short')),
    ];
    const max = all.reduce((m, b) => Math.max(m, b.notionalUsd), 0);
    if (max <= 0) return [];
    const logMax = Math.log10(max);
    for (const band of all) {
        // floor at ~1/1000 of the max so a tiny cluster still gets a faint band
        const ratio = Math.max(band.notionalUsd / max, 0.001);
        band.intensity = Math.max(0, 1 + Math.log10(ratio) / 3);
        void logMax;
    }
    return all;
}

function toBand(c: LiqCluster, side: Side): Band {
    return {
        side,
        price: c.price,
        low: c.low,
        high: c.high,
        notionalUsd: c.notionalUsd,
        wallets: c.wallets,
        distancePct: c.distancePct,
        intensity: 0,
    };
}

/**
 * The clusters that matter to a trader holding this side.
 *
 * A LONG is hurt by price FALLING, and the thing that makes a fall accelerate
 * is other longs being force-closed below — so the long clusters are the
 * danger and the short clusters above are where a rally would get squeezed
 * upward (a magnet). For a SHORT it is the mirror.
 */
export function relevantSides(type: PositionType): {
    danger: Side;
    magnet: Side;
} {
    return type === 'LONG'
        ? { danger: 'long', magnet: 'short' }
        : { danger: 'short', magnet: 'long' };
}

export type CheckKind =
    'stopInside' | 'stopNear' | 'liqInside' | 'liqNear' | 'clear';

export interface TradeCheck {
    kind: CheckKind;
    /** the cluster the finding is about; absent when clear */
    cluster: LiqCluster | null;
    /** distance from the user's level to the cluster's weighted price, in % of spot */
    gapPct: number | null;
    /** a level just past the cluster on the safe side, when a stop is the problem */
    suggestedStop: number | null;
}

/**
 * Whether the trader's own exit levels sit where the crowd gets liquidated.
 *
 * "Inside" means the level is within the cluster's price slice. "Near" means
 * within `nearPct` of the cluster's weighted price — close enough that the
 * wick which triggers the cluster reaches the level too. The stop is checked
 * first because it is the level the trader chose and can move; the
 * liquidation is checked second because it is the level they cannot.
 */
export function checkTrade(
    trade: {
        type: PositionType;
        stopLossPrice: number | null;
        liquidationPrice: number | null;
    },
    snapshot: LiqMapSnapshot | null | undefined,
    nearPct = 0.35,
): TradeCheck {
    const clear: TradeCheck = {
        kind: 'clear',
        cluster: null,
        gapPct: null,
        suggestedStop: null,
    };
    if (!snapshot || snapshot.spot <= 0) return clear;
    const { danger } = relevantSides(trade.type);
    const clusters = danger === 'long' ? snapshot.longs : snapshot.shorts;
    if (clusters.length === 0) return clear;
    const spot = snapshot.spot;

    const hit = (
        level: number | null,
        insideKind: CheckKind,
        nearKind: CheckKind,
    ): TradeCheck | null => {
        if (level === null || !Number.isFinite(level) || level <= 0)
            return null;
        for (const c of clusters) {
            const gapPct = (Math.abs(level - c.price) / spot) * 100;
            if (level >= c.low && level <= c.high) {
                return {
                    kind: insideKind,
                    cluster: c,
                    gapPct: round2(gapPct),
                    suggestedStop: suggestPast(c, trade.type, spot),
                };
            }
            if (gapPct <= nearPct) {
                return {
                    kind: nearKind,
                    cluster: c,
                    gapPct: round2(gapPct),
                    suggestedStop: suggestPast(c, trade.type, spot),
                };
            }
        }
        return null;
    };

    return (
        hit(trade.stopLossPrice, 'stopInside', 'stopNear') ??
        hit(trade.liquidationPrice, 'liqInside', 'liqNear') ??
        clear
    );
}

// a stop that sits on the far side of the cluster is not swept by the wick
// that triggers it: 0.2% past the cluster's outer edge, on the losing side
function suggestPast(c: LiqCluster, type: PositionType, spot: number): number {
    const pad = spot * 0.002;
    return type === 'LONG' ? c.low - pad : c.high + pad;
}

function round2(v: number): number {
    return Number(v.toFixed(2));
}

/** Cumulative notional force-closed within `pct` of spot on the falling (`down`) or rising (`up`) side. */
export function cascadeWithin(
    snapshot: LiqMapSnapshot | null | undefined,
    pct: number,
    direction: 'down' | 'up',
): number {
    if (!snapshot) return 0;
    const steps = snapshot.cascade[direction];
    let total = 0;
    for (const [p, v] of steps) if (p <= pct) total = v;
    return total;
}

/** "$27.6M", "$910K", "$4,200" */
export function formatNotional(usd: number): string {
    if (usd >= 1e9) return `$${(usd / 1e9).toFixed(2)}B`;
    if (usd >= 1e6) return `$${(usd / 1e6).toFixed(1)}M`;
    if (usd >= 1e3) return `$${(usd / 1e3).toFixed(0)}K`;
    return `$${Math.round(usd).toLocaleString('en-US')}`;
}

/**
 * The nearest band on each side that is entirely OUTSIDE the visible price
 * window. Both charts fit their y-range to the last hour of price — a window
 * a fraction of a percent tall — while clusters sit 1-15% away, so most of
 * the time there is no band inside the window at all. These are what the
 * chart pins to its top and bottom edge instead, so the nearest danger is
 * always readable without flattening the price line to fit it.
 */
export function offscreenMarkers(
    bands: readonly Band[],
    visible: { lo: number; hi: number },
): { below: Band | null; above: Band | null } {
    let below: Band | null = null;
    let above: Band | null = null;
    for (const band of bands) {
        if (band.side === 'long' && band.high < visible.lo) {
            if (!below || band.high > below.high) below = band;
        } else if (band.side === 'short' && band.low > visible.hi) {
            if (!above || band.low < above.low) above = band;
        }
    }
    return { below, above };
}

/** "$6.2M · 1.4% below" — what an edge marker says. */
export function edgeLabel(band: Band): string {
    const where = band.side === 'long' ? 'below' : 'above';
    return `${formatNotional(band.notionalUsd)} · ${Math.abs(band.distancePct).toFixed(1)}% ${where}`;
}
